import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { eq } from 'ember-truth-helpers';

import type { IconName } from '../../../types/components/prism/icon.ts';
import PrismBadge from '../badge.gts';
import PrismIcon from '../icon.gts';
import PrismTooltip from '../tooltip.gts';

interface PrismViewSwitcherButtonSignature {
  Args: {
    activeButtonId: string;
    badge?: string;
    icon?: IconName;
    id: string;
    isDisabled?: boolean;
    label: string;
    onSetActiveButtonId: (buttonId: string) => unknown;
    pseudo?: 'focus' | 'hover';
    tooltipText?: string;
  };
  Element: HTMLButtonElement;
}

export default class PrismViewSwitcherButton extends Component<PrismViewSwitcherButtonSignature> {
  get iconSize(): 'small' | 'medium' {
    const { label } = this.args;

    return label ? 'small' : 'medium';
  }

  get isIconOnly(): boolean {
    const { badge, label } = this.args;

    return !label && !badge;
  }

  @action onClick(): void {
    const { id, isDisabled, onSetActiveButtonId } = this.args;

    if (isDisabled) {
      return;
    }

    onSetActiveButtonId(id);
  }

  <template>
    <li class="prism-view-switcher-list-item">
      <button
        aria-disabled={{@isDisabled}}
        class="prism-view-switcher__button text-300--medium
          {{if
            (eq @activeButtonId @id)
            'prism-view-switcher__button--selected'
          }}
          {{if this.isIconOnly 'prism-view-switcher__button--icon-only'}}
          {{if @isDisabled 'prism-view-switcher__button--disabled'}}"
        data-test-pseudo={{@pseudo}}
        data-test-view-switcher-button={{@label}}
        type="button"
        {{on "click" this.onClick}}
        ...attributes
      >
        {{#if @icon}}
          <PrismIcon @color="inherit" @name={{@icon}} @size={{this.iconSize}} />
        {{/if}}

        {{@label}}

        {{#if @badge}}
          <PrismBadge @label={{@badge}} @type="brand" />
        {{/if}}

        {{#if @tooltipText}}
          <PrismTooltip @text={{@tooltipText}} />
        {{/if}}
      </button>
    </li>
  </template>
}
